import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
    ApiService,
    AuthService,
    BlockAbstractComponent,
    BrandingService,
    CacheService,
    PageService,
    ParserService,
    pickContrastingTextColor,
    SettingsService,
} from '@gallaghercp/core-frontend';
import { BsDropdownConfig } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

@Component({
    selector: 'core-client-block-language',
    templateUrl: './block-language.component.html',
    // Classic NgModule component (not standalone) to align with project policy.
    // NOTE: Template dependencies are provided by LayoutModule (CommonModule, TranslateModule, UtilsModule, etc.).
    standalone: false,
    providers: [{ provide: BsDropdownConfig, useValue: { isAnimated: false, autoClose: true } }],
})
export class BlockClientLanguageComponent extends BlockAbstractComponent implements OnInit {
    @ViewChild('languageSelect', { static: true }) languageModal!: TemplateRef<any>;

    languages$: any;
    zone!: string;
    modalRef?: BsModalRef;
    isLargeBreakpoint!: boolean;
    languageCurrentColor: string = 'var(--color-neutral-dark-grey)';
    showFlags: boolean = (this.configService.get('languageFlags') as boolean | undefined) ?? true;

    constructor(
        protected override parser: ParserService,
        protected override auth: AuthService,
        protected override pages: PageService,
        protected modalService: BsModalService,
        protected cache: CacheService,
        protected api: ApiService,
        protected breakpoint: BreakpointObserver,
        protected uiBranding: BrandingService,
        protected settings: SettingsService,
    ) {
        super(parser, auth, pages);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.push(
            this.settings.isDarkMode().subscribe(isDarkMode => {
                const colors = this.uiBranding.getBrandColors();
                const configColors = this.configService.get('colors') as any;

                const headerBg = colors?.bg ?? configColors?.elementBackgroundColour ?? configColors?.headerBg;

                const resolvedColor = headerBg || (isDarkMode ? '#000000' : '#ffffff');

                const contrastColor = pickContrastingTextColor(resolvedColor);
                this.languageCurrentColor = contrastColor === 'white' ? '#ffffff' : 'var(--color-neutral-dark-grey)';
            }),
        );

        this.subscriptions.push(
            this.translateService.getLanguages().subscribe((languages: any) => {
                this.languages$ = languages;
                this.applyAvailableLanguages(languages ?? []);
                this.zone = this.api.getServiceZone();
            }),
        );

        this.subscriptions.push(
            this.api.zoneChangeEmit.subscribe(() => {
                this.translateProvider.use(this.getCurrentLanguage());
                this.zone = this.api.getServiceZone();
            }),
        );

        this.subscriptions.push(
            this.breakpoint.observe(['(min-width: 993px)']).subscribe(result => {
                this.isLargeBreakpoint = result.matches;
            }),
        );
    }

    getCurrentLanguage(): string {
        return this.translateService.getCurrentLanguage() as string;
    }

    isMultiLanguage(): boolean {
        // Show the language selector whenever more than one language is available.
        // Previously this was gated by service zone !== '1', which hid the control
        // in default zone scenarios. Requirement: relax to languages-only check.
        return this.languages.length > 1;
    }

    getLanguageName(code: string): string {
        return (this.languages.filter((language: any) => language.code === code)[0] ?? {}).name;
    }

    setLanguage(language: { code: string }): void {
        this.translateService.setCurrentLanguage(language.code);
        if (!this.translateProvider.translations[language.code]) {
            this.translateProvider.setTranslation(language.code, {}, true);
        }
        this.translateProvider.use(language.code);
        this.closeModal();
    }

    openModal(template: TemplateRef<any>): void {
        if (!this.modalRef) {
            this.modalRef = this.modalService.show(template);
        }
    }

    closeModal(): void {
        if (this.modalRef) {
            this.modalRef.hide();
        }
    }

    getLanguageItemClasses(languageCode: string): string {
        const languageCodeClass: string = ['language-item--', languageCode].join('');
        return languageCode === this.getCurrentLanguage() ? 'active ' + languageCodeClass : languageCodeClass;
    }

    // Resolve the active language only once the available languages are known.
    // Previously `setLanguage({ code: 'en_GB' })` ran synchronously in `ngOnInit`,
    // before `languages` had loaded, which silently reset the language the user
    // had just selected (hence the intermittent behaviour).
    private applyAvailableLanguages(languages: { code: string }[]): void {
        const currentLanguage = this.getCurrentLanguage();

        // Nothing to validate against yet - keep the stored selection as is.
        if (!languages.length) {
            this.translateProvider.use(currentLanguage);
            return;
        }

        const isCurrentAvailable = languages.some(language => language.code === currentLanguage);
        const targetLanguage = isCurrentAvailable ? currentLanguage : languages[0].code;

        if (targetLanguage !== currentLanguage) {
            this.translateService.setCurrentLanguage(targetLanguage);
        }

        this.translateProvider.use(targetLanguage);
    }
}
